import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Eye, Trash2, Loader2, Clock, FilePlus, Sparkles } from "lucide-react";
import { artifactStyle, formatArtifactDate } from "./artifactModel";

const IconButton = ({ title, onClick, danger, children }) => (
  <Button size="sm" variant="ghost" className={`h-7 w-7 p-0 text-gray-400 ${danger ? "hover:text-red-400" : "hover:text-white"}`} onClick={onClick} title={title}>
    {children}
  </Button>
);

const ArtifactItem = ({ artifact, idx, onView, onDownload, onDelete }) => {
  const { Icon, text, badge } = artifactStyle(artifact.type);
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}
      className="glass-card-light rounded-lg p-3 group hover:bg-white/5 transition-colors">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5"><Icon className={`h-4 w-4 ${text}`} /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="text-sm text-white truncate">{artifact.name}</h4>
            <Badge className={`text-xs ${badge}`}>{artifact.type?.toUpperCase() || "FILE"}</Badge>
          </div>
          {artifact.description && <p className="text-xs text-gray-500 line-clamp-1 mb-1">{artifact.description}</p>}
          <div className="flex items-center gap-3 text-xs text-gray-600">
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{formatArtifactDate(artifact.createdAt)}</span>
            {artifact.size && <span>{artifact.size}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <IconButton title="View" onClick={() => onView(artifact)}><Eye className="h-3 w-3" /></IconButton>
          <IconButton title="Download" onClick={() => onDownload(artifact)}><Download className="h-3 w-3" /></IconButton>
          <IconButton title="Delete" danger onClick={() => onDelete(artifact.artifactId)}><Trash2 className="h-3 w-3" /></IconButton>
        </div>
      </div>
    </motion.div>
  );
};

const EmptyState = ({ onGenerate }) => (
  <div className="text-center py-8">
    <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center mx-auto mb-3">
      <FilePlus className="h-6 w-6 text-orange-400/50" />
    </div>
    <p className="text-gray-400 text-sm mb-1">No artifacts yet</p>
    <p className="text-gray-600 text-xs mb-3">Generate deliverables using AI</p>
    <Button size="sm" onClick={onGenerate} className="bg-ism-teal hover:bg-ism-teal/90">
      <Sparkles className="h-3 w-3 mr-1" />
      Create First Artifact
    </Button>
  </div>
);

export const ArtifactList = ({ loading, artifacts, onView, onDownload, onDelete, onGenerate }) => {
  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-orange-400" /></div>;
  if (artifacts.length === 0) return <EmptyState onGenerate={onGenerate} />;
  return artifacts.map((artifact, idx) => (
    <ArtifactItem key={artifact.artifactId || `${artifact.name}-${artifact.createdAt}`} artifact={artifact} idx={idx}
      onView={onView} onDownload={onDownload} onDelete={onDelete} />
  ));
};
